// src/utils/paginate.js
//
// Laying a manuscript out onto pages: one `.rv-page` sheet after another, each
// body filled until it overflows, the block that overflowed cut at the page
// bottom or moved to the next page.
//
// This lived inside EditorPage.jsx, tied to the editor's own element. It is here
// so the Theme Editor's preview can lay the same manuscript out at the theme's
// type and margins with the same code — one definition of where a page breaks,
// the way `manuscriptPages.js` is one definition of what a page is. Moved, not
// rewritten: the editor calls it exactly as it called its own copy.
//
// Everything here measures the live DOM, so the sheets must be rendered (not
// `display: none`) when it runs. Scaling them with a transform is fine — every
// measurement is taken in the same scaled space.

import { joinSplitBlocks, markSplit } from './splitBlocks';
import { columnWidthOf, fitImagesToColumn } from './imageFit';
import {
  bleedImageOf, dropStaleBleedIds, isWrapped, joinBleedPieces, splitBleedImage,
} from './imageBleed';

/** A new, empty page sheet: header, editable body with its end spacer, footer. */
export const createEmptyPageEl = (doc) => {
  const page = doc.createElement('section');
  page.className = 'rv-page';
  page.setAttribute('data-type', 'page');

  const header = doc.createElement('div');
  header.className = 'rv-page-header';
  header.setAttribute('contenteditable', 'false');
  header.innerHTML = '<div class="rv-header-content"></div>';

  const body = doc.createElement('div');
  body.className = 'rv-page-body';
  body.setAttribute('contenteditable', 'true');
  body.innerHTML = '<p><br/></p><p class="rv-end-spacer" contenteditable="false"><br/></p>';

  const footer = doc.createElement('div');
  footer.className = 'rv-page-footer';
  footer.setAttribute('contenteditable', 'false');
  footer.innerHTML = '<div class="rv-footer-content"></div>';

  page.appendChild(header);
  page.appendChild(body);
  page.appendChild(footer);
  return page;
};

export const isBodyOverflowing = (body) => {
  if (!body) return false;
  const page = body.closest('.rv-page');
  if (!page) return false;
  // Compare scrollHeight (actual content height) to clientHeight (visible height)
  // scrollHeight > clientHeight means content is overflowing
  // Use a small buffer (5px) to catch overflow early before content gets cut off
  return body.scrollHeight > body.clientHeight + 5;
};

const splitParagraphReturn = (para, bodyBottom) => {
  const doc = para.ownerDocument;
  const walker = doc.createTreeWalker(para, NodeFilter.SHOW_TEXT, null, false);
  const textNodes = [];
  let n = walker.nextNode();
  while (n) { textNodes.push(n); n = walker.nextNode(); }
  if (textNodes.length === 0) return null;

  let splitNode = null;
  let splitOffset = 0;
  for (const tn of textNodes) {
    const r = doc.createRange();
    for (let i = 0; i <= tn.length; i++) {
      r.setStart(tn, 0);
      r.setEnd(tn, i);
      const rect = r.getBoundingClientRect();
      if (rect.bottom > bodyBottom - 2) {
        splitNode = tn;
        splitOffset = Math.max(0, i - 1);
        const text = tn.textContent;
        for (let j = splitOffset; j > 0; j--) {
          if (text[j] === ' ') { splitOffset = j + 1; break; }
        }
        break;
      }
    }
    if (splitNode) break;
  }
  if (!splitNode || splitOffset <= 0) return null;

  const overflow = doc.createElement(para.tagName);
  for (const a of para.attributes) overflow.setAttribute(a.name, a.value);

  const r = doc.createRange();
  r.setStart(splitNode, splitOffset);
  r.setEndAfter(para.lastChild);
  overflow.appendChild(r.extractContents());

  if (!para.textContent.trim() && !para.querySelector('*')) {
    para.innerHTML = '<br/>';
  }
  return overflow;
};

const splitListReturn = (list, bodyBottom) => {
  const doc = list.ownerDocument;
  const items = Array.from(list.children).filter((c) => c.tagName === 'LI');
  if (items.length <= 1) return null;

  let splitIdx = -1;
  for (let i = 0; i < items.length; i++) {
    if (items[i].getBoundingClientRect().bottom > bodyBottom - 2) {
      splitIdx = i;
      break;
    }
  }
  if (splitIdx <= 0) return null;

  const overflow = doc.createElement(list.tagName);
  for (const a of list.attributes) overflow.setAttribute(a.name, a.value);
  if (list.tagName === 'OL') {
    const startNum = parseInt(list.getAttribute('start') || '1', 10);
    overflow.setAttribute('start', String(startNum + splitIdx));
  }
  for (let i = splitIdx; i < items.length; i++) overflow.appendChild(items[i]);
  return overflow;
};

const splitTableReturn = (wrap, bodyBottom) => {
  const doc = wrap.ownerDocument;
  const table = wrap.querySelector('.rv-table');
  const tbody = table?.querySelector('tbody');
  if (!tbody) return null;
  const rows = Array.from(tbody.children).filter((c) => c.tagName === 'TR');
  if (rows.length <= 1) return null;

  let splitIdx = -1;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].getBoundingClientRect().bottom > bodyBottom - 2) {
      splitIdx = i;
      break;
    }
  }
  if (splitIdx <= 0) return null;

  const newWrap = doc.createElement('div');
  newWrap.className = 'rv-table-wrap';
  newWrap.setAttribute('contenteditable', 'false');
  newWrap.style.width = wrap.style.width || '80%';

  const newTable = doc.createElement('table');
  newTable.className = 'rv-table';
  newTable.setAttribute('contenteditable', 'false');
  newTable.tabIndex = 0;

  const thead = table.querySelector('thead');
  if (thead) newTable.appendChild(thead.cloneNode(true));

  const newTbody = doc.createElement('tbody');
  for (let i = splitIdx; i < rows.length; i++) newTbody.appendChild(rows[i]);
  newTable.appendChild(newTbody);
  newWrap.appendChild(newTable);
  return newWrap;
};

export const trySplitBlock = (body, block) => {
  const cs = window.getComputedStyle(body);
  const padB = parseFloat(cs.paddingBottom || '0') || 0;
  const bodyBottom = body.getBoundingClientRect().bottom - padB;

  if (block.tagName === 'OL' || block.tagName === 'UL') {
    return splitListReturn(block, bodyBottom);
  }
  if (block.classList?.contains('rv-table-wrap')) {
    return splitTableReturn(block, bodyBottom);
  }
  // An image the author let continue onto the next page. A wrapped one is cut
  // by reflowPages itself, which has to hold its rest back; see there.
  const bleedSpan = bleedImageOf(block);
  if (bleedSpan && !isWrapped(bleedSpan)) {
    return splitBleedImage(body, block, bleedSpan);
  }
  // For everything else — paragraphs, headings, blockquotes, DIVs, SPANs,
  // arbitrary wrappers — fall back to text-node splitting. Any block with
  // text content can be split at a word boundary near the page bottom.
  if (block.textContent && block.textContent.trim().length > 0) {
    return splitParagraphReturn(block, bodyBottom);
  }
  return null;
};

/**
 * Lay every block inside `root`'s `.rv-page` sheets out afresh, in one pass.
 *
 *   1. Collect every direct child of every `.rv-page-body`, in order, and join
 *      the blocks the last pass cut at a page bottom.
 *   2. Clear each body down to its spacer.
 *   3. Place the blocks page by page; a block that overflows is cut at the page
 *      bottom, or moved whole to the next page if it cannot be cut.
 *   4. Drop the pages left unused at the end.
 *
 * Options, for a layout that has rules of its own (the Theme Editor's preview,
 * and the editor when a theme is applied):
 *   - `breakBefore(block)` — start this block on a new page;
 *   - `onRecto(block)` — and make that page a right-hand one, leaving the page
 *     before it blank when it would otherwise land on a left. Page 1 is a right.
 *
 * The caller owns everything the editor does around a layout — keeping the
 * caret, renumbering lists, page numbers.
 */
export function reflowPages(root, { breakBefore = null, onRecto = null } = {}) {
  if (!root) return;
  const allPages = Array.from(root.querySelectorAll('.rv-page'));
  if (allPages.length === 0) return;

  const doc = root.ownerDocument;

  // 1. Collect content blocks in document order. Running images are put
  // back together first, so the split below always cuts them afresh.
  const collected = [];
  allPages.forEach((page) => {
    const body = page.querySelector('.rv-page-body');
    if (!body) return;
    Array.from(body.children).forEach((child) => {
      if (!child.classList?.contains('rv-end-spacer')) collected.push(child);
    });
  });
  // Then every paragraph, list and table the last pass cut at a page bottom
  // is joined back, so this pass cuts afresh instead of adding breaks.
  const blocks = joinSplitBlocks(joinBleedPieces(collected));
  let splitSeq = 0;
  // An image wider than the text column (a smaller book size) is scaled down
  // to fit it before anything is measured.
  fitImagesToColumn(blocks, columnWidthOf(allPages[0].querySelector('.rv-page-body')));

  // Look only at direct children — a `.rv-end-spacer` nested inside a
  // previously-placed block must not be confused with the body's own
  // spacer (otherwise `insertBefore` blows up with a "not a child" error).
  const findDirectSpacer = (bodyEl) =>
    Array.from(bodyEl.children).find((c) =>
      c.classList?.contains('rv-end-spacer')
    );

  const stripToSpacerOnly = (bodyEl) => {
    const spacer = findDirectSpacer(bodyEl);
    Array.from(bodyEl.children).forEach((child) => {
      if (child !== spacer) bodyEl.removeChild(child);
    });
    if (!findDirectSpacer(bodyEl)) {
      const s = doc.createElement('p');
      s.className = 'rv-end-spacer';
      s.setAttribute('contenteditable', 'false');
      s.innerHTML = '<br/>';
      bodyEl.appendChild(s);
    }
  };

  // 2. Clear each body down to just its spacer.
  allPages.forEach((page) => {
    const body = page.querySelector('.rv-page-body');
    if (body) stripToSpacerOnly(body);
  });

  // 3. Distribute blocks into pages.
  let pageIdx = 0;
  let currentBody = allPages[0].querySelector('.rv-page-body');

  const placeAtCurrentPage = (block) => {
    if (!currentBody) return;
    const spacer = findDirectSpacer(currentBody);
    if (spacer) currentBody.insertBefore(block, spacer);
    else currentBody.appendChild(block);
  };

  const advanceToNextPage = () => {
    pageIdx++;
    if (pageIdx >= allPages.length) {
      const newPage = createEmptyPageEl(doc);
      allPages[allPages.length - 1].insertAdjacentElement('afterend', newPage);
      allPages.push(newPage);
    }
    currentBody = allPages[pageIdx].querySelector('.rv-page-body');
    // New pages from createEmptyPageEl ship with a template empty <p>
    // before the spacer. Strip it so the page starts as [spacer] only,
    // matching the cleared state of pre-existing pages.
    if (currentBody) stripToSpacerOnly(currentBody);
  };

  const pageHasContent = () => !!currentBody && Array.from(currentBody.children).some(
    (el) => !el.classList?.contains('rv-end-spacer')
  );

  // The rest of each wrapped image cut on this page. The text after a
  // wrapped image wraps beside its first piece, so the page keeps filling
  // after the cut; the rest waits here and goes first on the next page,
  // whenever that starts, for the text there to wrap beside it in turn.
  let pendingRests = [];
  const takePendingRests = () => {
    const r = pendingRests;
    pendingRests = [];
    return r;
  };

  // A block that has already been given its own page, so placing it again
  // after an overflow does not start yet another one.
  const started = new Set();

  let i = 0;
  let safety = 0;
  while (i < blocks.length || pendingRests.length) {
    if (++safety > 10000) {
      console.warn('[reflow] safety limit hit; aborting');
      break;
    }

    // Nothing left to place but the rest of a wrapped image: give it a page.
    if (i >= blocks.length) {
      advanceToNextPage();
      blocks.push(...takePendingRests());
      continue;
    }

    const block = blocks[i];

    // The rest of a block cut at a page bottom already starts a page, and is
    // not a second chapter opening.
    const isRest = block.hasAttribute?.('data-split-cont') || block.hasAttribute?.('data-bleed-cont');
    if (breakBefore && !isRest && !started.has(block) && !pendingRests.length && breakBefore(block)) {
      started.add(block);
      if (pageHasContent()) advanceToNextPage();
      // Page 1 is a right-hand page, so a right-hand page is an even index.
      if (onRecto && onRecto(block) && pageIdx % 2 === 1) advanceToNextPage();
    }

    placeAtCurrentPage(block);

    if (!isBodyOverflowing(currentBody)) {
      i++;
      continue;
    }

    // A wrapped image that runs on: cut it here and carry on filling this
    // page beside the first piece.
    const floatSpan = bleedImageOf(block);
    if (floatSpan && isWrapped(floatSpan)) {
      const rest = splitBleedImage(currentBody, block, floatSpan);
      if (rest) {
        pendingRests.push(rest);
        i++;
        continue;
      }
    }

    // Overflow. Try to split.
    const overflow = trySplitBlock(currentBody, block);
    if (overflow) {
      // Mark the cut so the next reflow joins it back. An image's rest
      // carries its own marks.
      if (!overflow.hasAttribute('data-bleed-cont')) {
        splitSeq += 1;
        markSplit(block, overflow, `s${splitSeq}`);
      }
      advanceToNextPage();
      blocks.splice(i + 1, 0, ...takePendingRests(), overflow);
      i++;
      continue;
    }

    // Can't split. If this block is alone on the page, let it overflow
    // visually — it's larger than a page (image, single huge cell, etc.).
    const contentChildren = Array.from(currentBody.children).filter(
      (el) => !el.classList?.contains('rv-end-spacer')
    );
    if (contentChildren.length === 1) {
      i++;
      advanceToNextPage();
      blocks.splice(i, 0, ...takePendingRests());
      continue;
    }

    // Otherwise pull it back and place it on the next page.
    block.remove();
    advanceToNextPage();
    blocks.splice(i, 0, ...takePendingRests());
    // Do NOT advance i — re-place the same block on the new page next loop
    // (after the rest of any wrapped image, which goes first).
  }

  // 4. Drop trailing unused pages.
  for (let p = allPages.length - 1; p > pageIdx; p--) {
    const body = allPages[p].querySelector('.rv-page-body');
    const hasRealContent = body && Array.from(body.children).some(
      (el) => !el.classList?.contains('rv-end-spacer')
    );
    if (!hasRealContent) allPages[p].remove();
  }

  // Images that came out of this pass whole carry no piece id.
  dropStaleBleedIds(root);
}
